import * as React from 'react';
import { useMemo } from 'react';

import {
  EmployeeCalendar,
  EmployeeCalendarEventLegend,
  EmployeeCalendarLegendKeys,
  EmployeeCalendarLegendRows,
  useEmployeeAbsenceCalendarConfig,
} from '~/components/EmployeeCalendar';
import {
  useDynamicAbsenceCalendarLegend,
  useLimitedAbsenceCalendarLegend,
} from '~/components/EmployeeCalendar/Absences';
import { SelectedEmployeeData } from '~/hooks/employee/useGetSelectedEmployeeData';
import {
  AbsencesCalendarProps,
  useAbsenceCalendarCommon,
} from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesCalendar/useAbsenceCalendarCommon';

const AbsencesCalendarFull: React.FC<AbsencesCalendarProps> = (
  props: AbsencesCalendarProps,
) => {
  const { allEvents, calendarPerson, range, containerWidth, isLoading } =
    useAbsenceCalendarCommon(props);

  const { dynamicLegends, getDynamicAbsenceLegend } =
    useDynamicAbsenceCalendarLegend({ events: allEvents });

  const calendarProps = useEmployeeAbsenceCalendarConfig({
    events: allEvents,
    eventColorLegends: dynamicLegends,
    getAbsenceLegend: getDynamicAbsenceLegend,
  });

  const legendRows = useMemo((): EmployeeCalendarLegendRows => {
    const getLegendKeys = (
      legends: EmployeeCalendarEventLegend[],
    ): EmployeeCalendarLegendKeys => legends.map((legend) => legend.key);

    const dynamicKeys = getLegendKeys(dynamicLegends);

    const otherKeys = getLegendKeys(
      calendarProps.legends?.filter(
        (legend) => !dynamicKeys.includes(legend.key),
      ) ?? [],
    );

    return [dynamicKeys, otherKeys];
  }, [dynamicLegends, calendarProps.legends]);

  const employeeCalendarProps = useMemo(() => {
    return {
      ...calendarProps,
      listEmployees: [calendarPerson],
      legendsConfig: {
        legendRows,
      },
    };
  }, [calendarProps, legendRows, calendarPerson]);

  return (
    <EmployeeCalendar
      containerWidth={containerWidth}
      startDate={range.start}
      endDate={range.end}
      {...employeeCalendarProps}
      isLoading={isLoading}
      hideEmployeeName={true}
    />
  );
};

const AbsencesCalendarLimited: React.FC<
  AbsencesCalendarProps & {
    employeeData?: SelectedEmployeeData;
  }
> = (
  props: AbsencesCalendarProps & {
    employeeData?: SelectedEmployeeData;
  },
) => {
  const { allEvents, calendarPerson, range, containerWidth, isLoading } =
    useAbsenceCalendarCommon(props);

  const { eventColorLegends, getAbsenceLegend } =
    useLimitedAbsenceCalendarLegend({ events: allEvents });

  const calendarProps = useEmployeeAbsenceCalendarConfig({
    events: allEvents,
    eventColorLegends,
    getAbsenceLegend,
  });

  const employeeCalendarProps = useMemo(() => {
    return {
      ...calendarProps,
      listEmployees: [calendarPerson],
    };
  }, [calendarProps, calendarPerson]);

  return (
    <EmployeeCalendar
      containerWidth={containerWidth}
      startDate={range.start}
      endDate={range.end}
      {...employeeCalendarProps}
      isLoading={isLoading}
      hideEmployeeName={true}
    />
  );
};

export const AbsencesCalendar: React.FC<AbsencesCalendarProps> = (
  props: AbsencesCalendarProps,
) => {
  const { accessMode } = props;

  switch (accessMode) {
    case 'full': {
      return <AbsencesCalendarFull {...props} />;
    }
    case 'limited': {
      return <AbsencesCalendarLimited {...props} />;
    }
    default:
      return <></>;
  }
};
